import React from 'react';
import {
    Dashboard as DashboardIcon,
    People as PeopleIcon,
    Straighten as MeasurementIcon,
    Assignment as ProjectIcon,
    Person as EmployeeIcon,
    Settings as SettingsIcon,
    AssignmentInd as AssignmentIcon,
    ManageAccounts as AdminIcon,
    EmojiEvents,
    Timeline as TimelineIcon,
    EventNote as EventNoteIcon,
    Visibility as VirtualFittingIcon,
    Receipt as InvoiceIcon,
    AccountBalance as GSTIcon,
    Payment as PaymentIcon,
    Business as BrandIcon,
    Assessment as ReportsIcon,
} from '@mui/icons-material';

// Type definitions
export interface MenuItem {
    text: string;
    icon: React.ComponentType;
    path: string;
}

export interface MenuGroup {
    key: string;
    title: string;
    icon: React.ComponentType;
    items: MenuItem[];
}

// Define all available permissions/pages
export const PERMISSIONS = {
    DASHBOARD: 'dashboard',
    CLIENTS: 'clients',
    MEASUREMENTS: 'measurements',
    PROJECTS: 'projects',
    EMPLOYEES: 'employees',
    STAGES: 'stages',
    ASSIGNMENTS: 'assignments',
    ADMIN_ASSIGNMENTS: 'admin_assignments',
    EMPLOYEE_OVERVIEW: 'employee_overview',
    TIME_REPORTS: 'time_reports',
    ATTENDANCE: 'attendance',
    LEADERBOARD: 'leaderboard',
    VIRTUAL_FITTING: 'virtual_fitting',
    // GST Billing permissions
    INVOICES: 'invoices',
    GST_CONFIGURATION: 'gst_configuration',
    HSN_MASTER: 'hsn_master',
    PAYMENTS: 'payments',
    CREDIT_NOTES: 'credit_notes',
    GST_REPORTS: 'gst_reports',
    // Brand management permissions
    BRAND_SETTINGS: 'brand_settings',
    SUBSCRIPTION_MANAGEMENT: 'subscription_management',
    BRAND_MANAGEMENT: 'brand_management', // Super admin only
} as const;

// Menu item configuration with grouping
export const MENU_GROUPS = {
    main: {
        title: 'Main',
        icon: DashboardIcon,
        items: [
            { permission: PERMISSIONS.DASHBOARD, text: 'Dashboard', icon: DashboardIcon, path: '/' },
            { permission: PERMISSIONS.CLIENTS, text: 'Clients', icon: PeopleIcon, path: '/clients' },
            { permission: PERMISSIONS.PROJECTS, text: 'Projects', icon: ProjectIcon, path: '/projects' },
            { permission: PERMISSIONS.MEASUREMENTS, text: 'Measurements', icon: MeasurementIcon, path: '/measurements' },
        ]
    },
    gst: {
        title: 'GST & Billing',
        icon: InvoiceIcon,
        items: [
            { permission: PERMISSIONS.INVOICES, text: 'Invoices', icon: InvoiceIcon, path: '/invoices' },
            { permission: PERMISSIONS.PAYMENTS, text: 'Payments', icon: PaymentIcon, path: '/payments' },
            { permission: PERMISSIONS.CREDIT_NOTES, text: 'Credit Notes', icon: InvoiceIcon, path: '/credit-notes' },
            { permission: PERMISSIONS.HSN_MASTER, text: 'HSN Master', icon: SettingsIcon, path: '/gst/hsn-master' },
            { permission: PERMISSIONS.GST_CONFIGURATION, text: 'GST Configuration', icon: GSTIcon, path: '/gst/configuration' },
            { permission: PERMISSIONS.GST_REPORTS, text: 'GST Reports', icon: ReportsIcon, path: '/gst/reports' },
        ]
    },
    team: {
        title: 'Team Management',
        icon: PeopleIcon,
        items: [
            { permission: PERMISSIONS.EMPLOYEES, text: 'Employees', icon: EmployeeIcon, path: '/employees' },
            { permission: PERMISSIONS.ASSIGNMENTS, text: 'Assignments', icon: AssignmentIcon, path: '/assignments' },
            { permission: PERMISSIONS.ADMIN_ASSIGNMENTS, text: 'Admin: Assignments', icon: AdminIcon, path: '/admin/assignments' },
            { permission: PERMISSIONS.EMPLOYEE_OVERVIEW, text: 'Employee Overview', icon: PeopleIcon, path: '/admin/employee-assignments' },
            { permission: PERMISSIONS.TIME_REPORTS, text: 'Time Reports', icon: TimelineIcon, path: '/admin/time-reports' },
            { permission: PERMISSIONS.ATTENDANCE, text: 'Attendance', icon: EventNoteIcon, path: '/admin/attendance' },
        ]
    },
    settings: {
        title: 'Settings & Brand',
        icon: SettingsIcon,
        items: [
            { permission: PERMISSIONS.BRAND_SETTINGS, text: 'Brand Settings', icon: BrandIcon, path: '/brand/settings' },
            { permission: PERMISSIONS.BRAND_MANAGEMENT, text: 'Brand Management', icon: AdminIcon, path: '/admin/brands' },
            { permission: PERMISSIONS.SUBSCRIPTION_MANAGEMENT, text: 'Subscription', icon: AdminIcon, path: '/brand/settings' },
            { permission: PERMISSIONS.STAGES, text: 'Stages', icon: SettingsIcon, path: '/stages' },
        ]
    },
    tools: {
        title: 'Tools',
        icon: VirtualFittingIcon,
        items: [
            { permission: PERMISSIONS.VIRTUAL_FITTING, text: 'Virtual Fitting', icon: VirtualFittingIcon, path: '/virtual-fitting' },
            { permission: PERMISSIONS.LEADERBOARD, text: 'Performance Leaderboard', icon: EmojiEvents, path: '/leaderboard' },
        ]
    }
};

// Legacy menu items for backward compatibility
export const MENU_ITEMS = {
    [PERMISSIONS.DASHBOARD]: { text: 'Dashboard', icon: DashboardIcon, path: '/' },
    [PERMISSIONS.CLIENTS]: { text: 'Clients', icon: PeopleIcon, path: '/clients' },
    [PERMISSIONS.MEASUREMENTS]: { text: 'Measurements', icon: MeasurementIcon, path: '/measurements' },
    [PERMISSIONS.PROJECTS]: { text: 'Projects', icon: ProjectIcon, path: '/projects' },
    [PERMISSIONS.EMPLOYEES]: { text: 'Employees', icon: EmployeeIcon, path: '/employees' },
    [PERMISSIONS.STAGES]: { text: 'Stages', icon: SettingsIcon, path: '/stages' },
    [PERMISSIONS.ASSIGNMENTS]: { text: 'Assignments', icon: AssignmentIcon, path: '/assignments' },
    [PERMISSIONS.ADMIN_ASSIGNMENTS]: { text: 'Admin: Assignments', icon: AdminIcon, path: '/admin/assignments' },
    [PERMISSIONS.EMPLOYEE_OVERVIEW]: { text: 'Employee Overview', icon: PeopleIcon, path: '/admin/employee-assignments' },
    [PERMISSIONS.TIME_REPORTS]: { text: 'Time Reports', icon: TimelineIcon, path: '/admin/time-reports' },
    [PERMISSIONS.ATTENDANCE]: { text: 'Attendance', icon: EventNoteIcon, path: '/admin/attendance' },
    [PERMISSIONS.LEADERBOARD]: { text: 'Performance Leaderboard', icon: EmojiEvents, path: '/leaderboard' },
    [PERMISSIONS.VIRTUAL_FITTING]: { text: 'Virtual Fitting', icon: VirtualFittingIcon, path: '/virtual-fitting' },
    // GST Billing menu items
    [PERMISSIONS.INVOICES]: { text: 'Invoices', icon: InvoiceIcon, path: '/invoices' },
    [PERMISSIONS.GST_CONFIGURATION]: { text: 'GST Configuration', icon: GSTIcon, path: '/gst/configuration' },
    [PERMISSIONS.HSN_MASTER]: { text: 'HSN Master', icon: SettingsIcon, path: '/gst/hsn-master' },
    [PERMISSIONS.PAYMENTS]: { text: 'Payments', icon: PaymentIcon, path: '/payments' },
    [PERMISSIONS.CREDIT_NOTES]: { text: 'Credit Notes', icon: InvoiceIcon, path: '/credit-notes' },
    [PERMISSIONS.GST_REPORTS]: { text: 'GST Reports', icon: ReportsIcon, path: '/gst/reports' },
    // Brand management menu items
    [PERMISSIONS.BRAND_SETTINGS]: { text: 'Brand Settings', icon: BrandIcon, path: '/brand/settings' },
    [PERMISSIONS.BRAND_MANAGEMENT]: { text: 'Brand Management', icon: AdminIcon, path: '/admin/brands' },
    [PERMISSIONS.SUBSCRIPTION_MANAGEMENT]: { text: 'Subscription', icon: AdminIcon, path: '/brand/settings' },
};

// Role configuration - maps role names to their permissions
export const ROLE_CONFIG = {
    // Admin roles (full access)
    admin: [
        PERMISSIONS.DASHBOARD,
        PERMISSIONS.CLIENTS,
        PERMISSIONS.MEASUREMENTS,
        PERMISSIONS.PROJECTS,
        PERMISSIONS.EMPLOYEES,
        PERMISSIONS.STAGES,
        PERMISSIONS.ASSIGNMENTS,
        PERMISSIONS.ADMIN_ASSIGNMENTS,
        PERMISSIONS.EMPLOYEE_OVERVIEW,
        PERMISSIONS.TIME_REPORTS,
        PERMISSIONS.ATTENDANCE,
        PERMISSIONS.LEADERBOARD,
        PERMISSIONS.VIRTUAL_FITTING,
        // GST Billing permissions
        PERMISSIONS.INVOICES,
        PERMISSIONS.GST_CONFIGURATION,
        PERMISSIONS.HSN_MASTER,
        PERMISSIONS.PAYMENTS,
        PERMISSIONS.CREDIT_NOTES,
        PERMISSIONS.GST_REPORTS,
        // Brand management permissions
        PERMISSIONS.BRAND_SETTINGS,
        PERMISSIONS.SUBSCRIPTION_MANAGEMENT,
    ],
    administrator: [
        PERMISSIONS.DASHBOARD,
        PERMISSIONS.CLIENTS,
        PERMISSIONS.MEASUREMENTS,
        PERMISSIONS.PROJECTS,
        PERMISSIONS.EMPLOYEES,
        PERMISSIONS.STAGES,
        PERMISSIONS.ASSIGNMENTS,
        PERMISSIONS.ADMIN_ASSIGNMENTS,
        PERMISSIONS.EMPLOYEE_OVERVIEW,
        PERMISSIONS.TIME_REPORTS,
        PERMISSIONS.ATTENDANCE,
        PERMISSIONS.LEADERBOARD,
        PERMISSIONS.VIRTUAL_FITTING,
        // GST Billing permissions
        PERMISSIONS.INVOICES,
        PERMISSIONS.GST_CONFIGURATION,
        PERMISSIONS.HSN_MASTER,
        PERMISSIONS.PAYMENTS,
        PERMISSIONS.CREDIT_NOTES,
        PERMISSIONS.GST_REPORTS,
        // Brand management permissions
        PERMISSIONS.BRAND_SETTINGS,
        PERMISSIONS.SUBSCRIPTION_MANAGEMENT,
    ],
    'super admin': [
        PERMISSIONS.DASHBOARD,
        PERMISSIONS.CLIENTS,
        PERMISSIONS.MEASUREMENTS,
        PERMISSIONS.PROJECTS,
        PERMISSIONS.EMPLOYEES,
        PERMISSIONS.STAGES,
        PERMISSIONS.ASSIGNMENTS,
        PERMISSIONS.ADMIN_ASSIGNMENTS,
        PERMISSIONS.EMPLOYEE_OVERVIEW,
        PERMISSIONS.TIME_REPORTS,
        PERMISSIONS.ATTENDANCE,
        PERMISSIONS.LEADERBOARD,
        PERMISSIONS.VIRTUAL_FITTING,
        // GST Billing permissions
        PERMISSIONS.INVOICES,
        PERMISSIONS.GST_CONFIGURATION,
        PERMISSIONS.HSN_MASTER,
        PERMISSIONS.PAYMENTS,
        PERMISSIONS.CREDIT_NOTES,
        PERMISSIONS.GST_REPORTS,
        // Brand management permissions
        PERMISSIONS.BRAND_SETTINGS,
        PERMISSIONS.BRAND_MANAGEMENT, // Super admin only
        PERMISSIONS.SUBSCRIPTION_MANAGEMENT,
    ],
    
    // Designer role
    designer: [
        PERMISSIONS.DASHBOARD,
        PERMISSIONS.CLIENTS,
        PERMISSIONS.MEASUREMENTS,
        PERMISSIONS.PROJECTS,
        PERMISSIONS.ASSIGNMENTS,
        PERMISSIONS.TIME_REPORTS,
        PERMISSIONS.LEADERBOARD,
        PERMISSIONS.VIRTUAL_FITTING,
        // Limited GST access for designers
        PERMISSIONS.INVOICES,
        PERMISSIONS.PAYMENTS,
        PERMISSIONS.GST_REPORTS,
    ],
    
    // Employee role (basic access)
    employee: [
        PERMISSIONS.ASSIGNMENTS,
        PERMISSIONS.LEADERBOARD,
    ],
    
    // Future roles can be easily added here
    // manager: [
    //     PERMISSIONS.DASHBOARD,
    //     PERMISSIONS.ASSIGNMENTS,
    //     PERMISSIONS.TIME_REPORTS,
    //     PERMISSIONS.LEADERBOARD,
    // ],
    // supervisor: [
    //     PERMISSIONS.ASSIGNMENTS,
    //     PERMISSIONS.EMPLOYEE_OVERVIEW,
    //     PERMISSIONS.TIME_REPORTS,
    //     PERMISSIONS.LEADERBOARD,
    // ],
};

// Default redirect paths for each role type
export const ROLE_DEFAULT_PATHS = {
    admin: '/',
    administrator: '/',
    'super admin': '/',
    designer: '/',
    employee: '/assignments',
    // manager: '/',
    // supervisor: '/admin/employee-assignments',
};

// Utility functions
export const normalizeRoleName = (role?: string): string => {
    return role?.toLowerCase() || '';
};

export const getRolePermissions = (role?: string): string[] => {
    const normalizedRole = normalizeRoleName(role);
    return ROLE_CONFIG[normalizedRole as keyof typeof ROLE_CONFIG] || ROLE_CONFIG.employee;
};

export const hasPermission = (userRole: string | undefined, permission: string): boolean => {
    const permissions = getRolePermissions(userRole);
    return permissions.includes(permission);
};

export const getMenuItemsForRole = (role?: string) => {
    const permissions = getRolePermissions(role);
    return permissions.map(permission => 
        MENU_ITEMS[permission as keyof typeof MENU_ITEMS]
    );
};

/**
 * Get grouped menu items for a specific role
 */
export const getGroupedMenuItemsForRole = (role?: string): MenuGroup[] => {
    const userPermissions = getRolePermissions(role);
    const userPermissionSet = new Set(userPermissions);
    
    const groupedMenus: MenuGroup[] = [];
    
    Object.entries(MENU_GROUPS).forEach(([groupKey, group]) => {
        const availableItems = group.items.filter(item => 
            userPermissionSet.has(item.permission)
        ).map(item => ({
            text: item.text,
            icon: item.icon,
            path: item.path
        }));
        
        if (availableItems.length > 0) {
            groupedMenus.push({
                key: groupKey,
                title: group.title,
                icon: group.icon,
                items: availableItems
            });
        }
    });
    
    return groupedMenus;
};

export const getDefaultPathForRole = (role?: string): string => {
    const normalizedRole = normalizeRoleName(role);
    return ROLE_DEFAULT_PATHS[normalizedRole as keyof typeof ROLE_DEFAULT_PATHS] || '/assignments';
};

export const isAdminRole = (role?: string): boolean => {
    return hasPermission(role, PERMISSIONS.ADMIN_ASSIGNMENTS);
};

export const isDesignerOrAdminRole = (role?: string): boolean => {
    return hasPermission(role, PERMISSIONS.DASHBOARD);
}; 